/**
 * Spreadsheet parsing adapter for the bulk import (CT-5634). Client-side only: nothing is
 * uploaded. `.csv` is parsed natively; `.xlsx` goes through `read-excel-file`, loaded lazily
 * so the library stays out of the main chunk.
 *
 * CSV parsing runs in a Web Worker when the browser has one (the UI stays responsive), with a
 * main-thread fallback (tests, or a worker that cannot start). `.xlsx` is read by
 * `read-excel-file`, which uses its own worker.
 */
import { IMPORT_ERROR, MAX_BYTES } from '../shared/import/importLimits.js';
import { buildSheet, ImportParseError } from '../shared/import/buildSheet.js';
import { parseCsv } from '../shared/import/parseCsv.js';

const extensionOf = (name = '') => name.slice(name.lastIndexOf('.')).toLowerCase();

/** The worker could not start (unsupported, blocked, bundler): the caller falls back to the main thread. */
class WorkerUnavailableError extends Error {}

const hasWorker = () => typeof Worker === 'function';

/** Parses a CSV in `parseCsv.worker.js`; rejects with `ImportParseError` if it cannot read it. */
const parseCsvInWorker = (file) =>
  new Promise((resolve, reject) => {
    const worker = new Worker(new URL('../shared/import/parseCsv.worker.js', import.meta.url), {
      type: 'module',
    });
    const finish = (settle, value) => {
      worker.terminate();
      settle(value);
    };
    worker.onmessage = ({ data }) =>
      data.ok
        ? finish(resolve, data.matrix)
        : finish(reject, new ImportParseError(IMPORT_ERROR.UNREADABLE));
    worker.onerror = () => finish(reject, new WorkerUnavailableError());
    worker.postMessage({ file });
  });

const readText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new ImportParseError(IMPORT_ERROR.UNREADABLE));
    reader.readAsText(file);
  });

/** CSV matrix: in the worker when possible, on the main thread otherwise. */
const readCsvMatrix = async (file) => {
  if (hasWorker()) {
    try {
      return await parseCsvInWorker(file);
    } catch (error) {
      if (!(error instanceof WorkerUnavailableError)) throw error;
    }
  }
  return parseCsv(await readText(file));
};

/**
 * Default `.xlsx` reader (first sheet). A failure surfaces as an unreadable file, never a crash.
 *
 * @param {File} file
 * @returns {Promise<Array<Array<string|number|null>>>}
 */
const readXlsxWithLibrary = async (file) => {
  const { readSheet } = await import('read-excel-file/browser');
  return readSheet(file);
};

/**
 * Reads a `.csv` or `.xlsx` file into the sheet the wizard stores.
 * TODO(CT-5634 / backend): the backend may re-validate type, size and rows (ARP-1480).
 *
 * @param {File} file
 * @param {{ readXlsx?: (file: File) => Promise<Array<Array<unknown>>>, columnLabel?: (position: number) => string }} [adapters]
 *   `readXlsx` is a test seam; `columnLabel` names columns whose header is blank (translated by the UI).
 * @returns {Promise<ReturnType<typeof buildSheet>>}
 * @throws {ImportParseError} `unsupported_type | too_large | too_many_rows | empty | unreadable`
 */
export async function parseSpreadsheet(file, { readXlsx = readXlsxWithLibrary, columnLabel } = {}) {
  const extension = extensionOf(file.name);
  if (extension !== '.csv' && extension !== '.xlsx') {
    throw new ImportParseError(IMPORT_ERROR.UNSUPPORTED_TYPE);
  }
  if (file.size > MAX_BYTES) throw new ImportParseError(IMPORT_ERROR.TOO_LARGE);

  let matrix;
  try {
    matrix = extension === '.csv' ? await readCsvMatrix(file) : await readXlsx(file);
  } catch (error) {
    throw error instanceof ImportParseError ? error : new ImportParseError(IMPORT_ERROR.UNREADABLE);
  }
  return buildSheet(matrix, { columnLabel });
}
